/**
 * @file  contract/contract.model.ts
 * @description  defines the model export interface for contracts
 *
 * @author ogarcia (ozkary)
 *
 */
import Entity from '../models/entity.model';
import Product from '../product/product.model';
import Vehicle from '../vehicle/vehicle.model';
import { Plan as PaymentPlan } from '../paymentPlan/paymentPlan.model';
import { Customer } from '../customer/customer.model';
import Signature from '../../services/signature/model';

/**
 * the possible transactin types
 */
export enum TransactionType {
  Draft = 'GENERATE_REVIEW_COPY',
  Add = 'ADD',
  Remit = 'ADD_ACTIVE'
}

/**
 * transaction information
 * action: GENERATE_REVIEW_COPY for draft ADD for real contract
 */
export interface Transaction {
  action: TransactionType;
  created?: Date;
}

/**
 * contract information with reference to customer
 * Use this to store the information (SetContract API)
 */
export interface Contract extends ContractBase {
  dealerId: string;
  contractId: string;
  vehicle: Vehicle;
  customerId: string;
  contractPDF: string;
  transID: string;
  errors?: string[];
  messages?: string[];
  premiumFinancePDF: string;
  premiumFinanceQuoteID: string;
  pending?: Pending;
  status?: boolean;
  expirationDate?: Date;
  expirationMiles?: number;
}

/**
 * model for the contract request (SubmitContract API)
 */
export interface ContractRequest extends ContractBase {
  customer: Customer;
}

/**
 * model for the contract request (SubmitContract API)
 */
export interface ContractBase extends Entity {
  product: Product;
  transaction: Transaction;
  paymentPlan: PaymentPlan;
  customerCost: number;
  contractNumber?: string;
  signature?: Signature;
  dealId?: string;
}

/**
 * gets a contract number using the program configuration
 */
export interface ContractNumberRequest {
  programPrefix: string;
  programValue: string;
}

/**
 * the new contract number
 */
export interface ContractNumber {
  value: string;
  errors?: string[];
}

/**
 * pending contract information
 */
export interface Pending {
  prefix: string;
  value: string;
  number: string;
}

/**
 * existent contract information
 * status - check if active, expired, renewed
 */
export interface ContractInfo {
  contractNumber: string;
  status: string;
  expirationDate: Date;
  expirationMiles: number;
  dealerName: string;
  dealerNumber: string;
}

/**
 * contrail detail information
 * Check status:true for a successful result
 * Plan provides coverage information
 * Contract.status provides the contract status
 */
export interface ContractDetails {
  status: boolean;
  message: string;
  errors: string[];
  customer: Customer;
  plan: Product;
  vehicle: Vehicle;
  contract: ContractInfo;
}

/**
 * contract token that can come from a query string
 * Use in case there is an integration via  a link
 */
export interface ContractToken {
  id: string;
  vin: string;
  token?: string;
}
